import { useEffect, useRef, useState } from "react";
import { Mic, CheckCircle2, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import api from "../api";
import SalesCertProctorHud from "./SalesCertProctorHud";
import { proctorMicBus, setMicInUse } from "../lib/proctorMicBus";

/**
 * Shared 5-second mic-check gate — plays the exact same "record → Whisper →
 * confirm" flow before ANY audio-scored section (Sales Pitch, Role Play, etc.).
 *
 * Contract:
 *   • Parent renders this component when the candidate is about to hit a mic-
 *     dependent step.
 *   • On successful mic check (or the candidate explicitly opting to continue),
 *     parent's onPass() fires and the parent transitions to the real section.
 *
 * iter50 — the mic-check also captures a live webcam selfie during the 5 s
 * recording and ships it alongside the audio. The backend compares it to the
 * enrolled ID selfie BEFORE persisting the voice embedding, so a different
 * person can't quietly enrol their voice as the candidate's reference.
 *
 * Why a shared component instead of inline JSX per-page:
 *   • Prevents the two mic-check UIs drifting apart when we tweak copy or
 *     recording behaviour.
 *   • One place to update Whisper endpoint, timeslice, mime-type preference, etc.
 *   • Consistent look across pitch + role play so candidates learn it once.
 */
export default function MicCheckGate({
  title = "Let's check your mic first",
  subtitle = "We'll record 5 seconds of you speaking a test phrase and read it back so you know your mic is working. This is not scored.",
  testPhrase = "Testing one two three — this is my voice check.",
  proceedLabel = "Perfect — continue",
  proceedDisabled = false,
  onPass,
  onSecurityHold,
}) {
  const [phase, setPhase] = useState("idle");    // idle | recording | uploading | result
  const [micLevel, setMicLevel] = useState(0);   // 0..1 for live VU meter
  const [transcript, setTranscript] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [err, setErr] = useState("");
  // iter50 — face/identity binding state. `faceMismatch` is a hard block
  // (voice is not enrolled, candidate must retake with the enrolled person
  // on camera); after 3 strikes the backend opens a SECURITY_HOLD.
  const [faceMismatch, setFaceMismatch] = useState(false);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const activeStreamRef = useRef(null);
  // iter50 — hidden webcam stream used to capture a verification selfie
  // during the mic-check. Separate from the mic stream so stopping one
  // doesn't release the other.
  const videoStreamRef = useRef(null);
  const videoElRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  // iter45 — auto-retry a short-clip once with a fresh mic.
  // iter49 — bumped the retry budget from 1 → 3 and the inter-retry
  // stream-release wait from 350 → 800 ms. Device-specific regression
  // reports showed some mics (lower-end Android Chrome, bluetooth
  // headsets, some external USB mics on macOS) genuinely take 500-800 ms
  // after `getUserMedia` resolves before they start producing audible
  // samples, so a single 350 ms retry wasn't enough. Reset on every
  // user-initiated start() and every explicit Retry click.
  const MIC_AUTO_RETRY_MAX = 3;
  const MIC_RETRY_WAIT_MS = 800;
  const autoRetryCountRef = useRef(0);

  // Fully release any stream/context we currently hold. Safe to call
  // multiple times — all steps are null-guarded.
  const releaseAll = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (activeStreamRef.current) {
      try { activeStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
      activeStreamRef.current = null;
    }
    const ctx = audioCtxRef.current;
    if (ctx && ctx.state !== "closed") {
      try { ctx.close(); } catch (_) { /* noop */ }
    }
    audioCtxRef.current = null;
  };

  // iter50 — release the webcam stream separately so the audio-stream
  // release path can't accidentally stop it mid-selfie-capture.
  const releaseCamera = () => {
    if (videoStreamRef.current) {
      try { videoStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
      videoStreamRef.current = null;
    }
    setCameraReady(false);
  };

  // iter50 — acquire the webcam on mount so the live selfie preview is
  // visible to the candidate the entire time they're on the mic-check
  // screen. Not a hard requirement for the mic-check to succeed — if the
  // camera fails to open, we fall back to audio-only (backend logs the
  // absence as a face-check skip and relies on the earlier FaceRecheckGate
  // pass).
  const acquireCamera = async () => {
    if (videoStreamRef.current) return;
    try {
      const v = await navigator.mediaDevices.getUserMedia({
        video: { width: 320, height: 240, facingMode: "user" },
        audio: false,
      });
      videoStreamRef.current = v;
      if (videoElRef.current) {
        videoElRef.current.srcObject = v;
        const p = videoElRef.current.play();
        if (p?.catch) p.catch(() => {});
      }
      setCameraReady(true);
    } catch (_) {
      // Camera denied / in-use — proceed without the identity-binding
      // selfie. The user will be caught by the next FaceRecheckGate /
      // voice-match check if they are not the enrolled candidate.
      setCameraReady(false);
    }
  };

  // Grab a single JPEG snapshot from the live webcam feed. Returns a
  // base64 payload (no `data:` prefix) ready for FormData, or null when
  // the camera is unavailable.
  const captureSelfie = () => {
    const v = videoElRef.current;
    const c = canvasRef.current;
    if (!v || !c || !videoStreamRef.current) return null;
    if (!v.videoWidth || !v.videoHeight) return null;
    const w = 320;
    const h = Math.round((v.videoHeight / v.videoWidth) * 320);
    c.width = w;
    c.height = h;
    c.getContext("2d").drawImage(v, 0, 0, w, h);
    try {
      return c.toDataURL("image/jpeg", 0.8).split(",")[1] || null;
    } catch (_) {
      return null;
    }
  };

  // iter45 — on every mount, aggressively release any lingering stream the
  // previous section may have left in flight (defense-in-depth on top of
  // iter43's useProctor cleanup). The bus 'release' is published alongside
  // so the proctor hook knows the mic is free again when we unmount.
  // iter50 — also acquire the webcam early so the live preview and
  // selfie-capture are ready the moment the candidate taps "Start recording".
  useEffect(() => {
    releaseAll();
    acquireCamera();
    return () => {
      releaseAll();
      releaseCamera();
      setMicInUse(false);
      proctorMicBus.publish("release");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async (opts = {}) => {
    setErr("");
    const isAutoRetry = Boolean(opts.autoRetry);
    if (!isAutoRetry) {
      // Human-initiated start — reset the auto-retry budget and release any
      // orphan stream we may still be holding from a previous attempt.
      autoRetryCountRef.current = 0;
      releaseAll();
    }
    // iter45 — tell useProctor (via the bus + flag) to pause its slice
    // recorder. Two concurrent MediaRecorders on the same mic trip the
    // "muted later opener" bug on mobile Chrome / Safari.
    setMicInUse(true);
    proctorMicBus.publish("acquire");
    // One tick of micro-delay so the proctor's recorder has a chance to
    // stop before we open our own stream. Harmless on fast devices,
    // meaningfully reduces the race window on slow ones.
    await new Promise((r) => setTimeout(r, 60));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      activeStreamRef.current = stream;

      // Live VU meter via WebAudio
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      audioCtxRef.current = ctx;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        setMicLevel(Math.min(1, Math.sqrt(sum / buf.length) * 3));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      const preferred = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", ""];
      const mime = preferred.find((t) => !t || (window.MediaRecorder && MediaRecorder.isTypeSupported(t))) || "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
      rec.onstop = async () => {
        // iter45 — release the stream + audio context in one place so the
        // cleanup path can't drift between the normal and auto-retry paths.
        releaseAll();
        setPhase("uploading");
        const blob = new Blob(chunks, { type: mime || "audio/webm" });
        // iter50 — snap a live selfie from the webcam feed. If the camera
        // was blocked / unavailable this returns null, in which case the
        // backend skips the face-identity bind (soft fallback — the voice
        // will still be verified against the ID selfie on each turn).
        const selfieB64 = captureSelfie();
        // Mobile networks (esp. 3G/patchy 4G) plus a cold Whisper pod can
        // push the transcribe round-trip past axios' default 30 s. Give the
        // call a generous 60 s budget and, on client-side timeout, silently
        // retry once before surfacing an error to the candidate. We only
        // retry ECONNABORTED / network errors — a real 4xx/5xx response
        // is a genuine failure and should be shown immediately.
        const uploadOnce = async () => {
          const fd = new FormData();
          fd.append("audio", blob, "miccheck.webm");
          if (selfieB64) fd.append("selfie_base64", selfieB64);
          return api.post("/participant/miccheck/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
        };
        const isTransientTimeout = (err) =>
          err?.code === "ECONNABORTED" ||
          err?.name === "CanceledError" ||
          err?.name === "AbortError" ||
          (err?.message === "Network Error" && !err?.response);
        try {
          let r;
          try {
            r = await uploadOnce();
          } catch (e1) {
            if (isTransientTimeout(e1)) {
              r = await uploadOnce();          // one silent retry
            } else {
              throw e1;
            }
          }
          setTranscript(r.data.transcript || "");
          setWordCount(r.data.word_count || 0);
          // iter50 — face/identity binding: if the backend reports the
          // live selfie doesn't match the enrolled ID, do NOT advance. The
          // voice is NOT enrolled on the server either, so retake is the
          // only recovery path. After 3 strikes the backend already opens
          // a SECURITY_HOLD and that path flows through the 423 catch
          // handler below.
          if (r.data.face_mismatch) {
            setFaceMismatch(true);
            setErr(r.data.retake_reason || "The person on camera doesn't match the verified ID.");
            setPhase("idle");
            return;
          }
          setFaceMismatch(false);
          // iter44 — the mic-check rejects clips below the minimum
          // enrolment length. Surface a clear retake prompt so the
          // candidate speaks for longer rather than progressing with a
          // low-quality voice signature that would cause false-positive
          // mismatches during the role play.
          //
          // iter45 → iter49 — before surfacing that prompt, auto-retry up
          // to MIC_AUTO_RETRY_MAX times with a fresh getUserMedia stream.
          // The "waveform frozen → short clip" regression is caused by
          // a stale/muted track from the previous section leaking into
          // our stream OR by the mic hardware itself taking 500-800 ms to
          // start producing audible samples. Releasing + re-acquiring the
          // mic clears both. Only after every retry also comes back short
          // do we actually ask the candidate to speak longer.
          if (r.data.enrolment_too_short) {
            if (autoRetryCountRef.current < MIC_AUTO_RETRY_MAX) {
              autoRetryCountRef.current += 1;
              // eslint-disable-next-line no-console
              console.info(
                `[mic-check] enrolment too short — retry ${autoRetryCountRef.current}/${MIC_AUTO_RETRY_MAX} ` +
                `after ${MIC_RETRY_WAIT_MS}ms stream release`
              );
              // Longer delay so slower mics (Bluetooth headsets, some
              // external USB mics, lower-end Android) have time to fully
              // release and reacquire. The 350 ms wait we shipped in
              // iter45 wasn't enough on these devices.
              await new Promise((resolve) => setTimeout(resolve, MIC_RETRY_WAIT_MS));
              setPhase("idle");
              setMicLevel(0);
              setTranscript("");
              setWordCount(0);
              start({ autoRetry: true });
              return;
            }
            setErr(r.data.retake_reason || "Please speak for a few more seconds and retake.");
            setPhase("idle");
          } else {
            setPhase("result");
          }
        } catch (e) {
          // iter50 — 423 after 3 face-mismatch strikes = SECURITY_HOLD.
          // Parse the hold id and hand off to the parent's hard-stop
          // handler if provided; otherwise fall back to the inline
          // error so the candidate still sees why the check failed.
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail || String(e);
          const m = /SECURITY_HOLD:([0-9a-fA-F-]{36})/.exec(String(detail));
          if (status === 423 && m && typeof onSecurityHold === "function") {
            onSecurityHold({ holdId: m[1], reason: "face_mismatch_at_voice_enrol" });
            return;
          }
          setErr(detail);
          setPhase("idle");
        }
      };
      // Defensive — iter43: wrap rec.start() so NotSupportedError /
      // InvalidStateError (e.g. a stale MediaRecorder from a previous
      // section still holding the mic) can never surface as a raw React
      // dev-overlay. On failure we just show a friendly retry message.
      try {
        rec.start(500);
      } catch (e) {
        releaseAll();
        setMicInUse(false);
        proctorMicBus.publish("release");
        setErr("Microphone is temporarily busy — please tap Start again in a moment.");
        setPhase("idle");
        return;
      }
      setPhase("recording");
      // Auto-stop after 5 seconds
      setTimeout(() => { if (rec.state === "recording") rec.stop(); }, 5000);
    } catch (e) {
      releaseAll();
      setMicInUse(false);
      proctorMicBus.publish("release");
      setErr("Microphone access denied. Please allow mic access and reload.");
    }
  };

  const retry = () => {
    autoRetryCountRef.current = 0;
    setFaceMismatch(false);
    setTranscript(""); setWordCount(0); setMicLevel(0); setErr(""); setPhase("idle");
  };

  const looksGood = wordCount >= 3;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6 fade-in" data-testid="miccheck-gate">
      <SalesCertProctorHud />
      {/* iter50 — hidden canvas used to snapshot the webcam during recording. */}
      <canvas ref={canvasRef} className="hidden" aria-hidden />
      <div>
        <div className="overline mb-2">MIC CHECK · 5 SECONDS · NOT SCORED · IDENTITY-BOUND</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">{title}</h1>
        <p className="text-sm text-[#525252] mt-2 leading-relaxed">{subtitle}</p>
      </div>

      {/* iter50 — live webcam preview panel. Visible across every phase so
          the candidate can see themselves on camera the whole time the mic
          check is in progress. If the camera is blocked/unavailable we fall
          back to a plain mic-only flow and show a soft warning. */}
      <div className="card p-4 flex items-center gap-4" data-testid="miccheck-camera-panel">
        <div className="w-32 h-24 sm:w-40 sm:h-30 bg-[#0A0A0A] overflow-hidden flex-shrink-0 relative">
          <video
            ref={videoElRef}
            muted
            playsInline
            autoPlay
            className="w-full h-full object-cover"
            data-testid="miccheck-camera-video"
          />
          {cameraReady && (
            <div className="absolute top-1 left-1 bg-[#EE2737] text-white text-[9px] px-1.5 py-0.5 uppercase tracking-widest font-bold">
              LIVE
            </div>
          )}
        </div>
        <div className="text-xs text-[#525252] leading-relaxed">
          {cameraReady ? (
            <>
              <div className="font-bold text-[#0A0A0A] mb-1">Camera on — identity will be re-verified</div>
              We&apos;ll snap a quick selfie during this mic check and compare it to the ID you uploaded.
              Only the enrolled candidate can continue past this screen.
            </>
          ) : (
            <>
              <div className="font-bold text-[#B45309] mb-1">Camera unavailable</div>
              The mic check will still work, but we won&apos;t be able to re-verify your face here.
              You&apos;ll be asked for a face check again before you start the next section.
            </>
          )}
        </div>
      </div>

      {/* iter50 — face-identity mismatch error block (hard block). Shown
          when the backend confirms the live selfie doesn't match the ID
          selfie. Explicitly tells the enrolled candidate to retake. */}
      {faceMismatch && (
        <div
          className="card p-4 border-l-4 border-[#DC2626] bg-[#FEF2F2] flex items-start gap-3"
          data-testid="miccheck-face-mismatch"
        >
          <ShieldAlert size={20} className="text-[#DC2626] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-[#7F1D1D] leading-relaxed">
            <div className="font-bold mb-1">Identity check didn&apos;t pass</div>
            The person on camera doesn&apos;t match the ID verified at the start of this attempt.
            Only the enrolled candidate can complete this mic check — please have them sit in front
            of the camera and retake. After three failed attempts this assessment will be paused
            for admin review.
          </div>
        </div>
      )}

      {phase === "idle" && (
        <>
          <div className="card p-6 bg-[#F8F9FA]">
            <div className="overline mb-2">SAY THIS OUT LOUD, THEN CLICK START</div>
            <p className="text-sm text-[#0A0A0A] italic leading-relaxed">&ldquo;{testPhrase}&rdquo;</p>
          </div>
          {err && (
            <div className="card p-4 border-l-4 border-[#EE2737] bg-[#FEF2F2]">
              <p className="text-sm text-[#991B1B]">{err}</p>
            </div>
          )}
          <button
            onClick={start}
            className="btn btn-primary text-sm w-full sm:w-auto justify-center"
            data-testid="miccheck-start"
          >
            <Mic size={14} /> Start 5-second mic check
          </button>
        </>
      )}

      {phase === "recording" && (
        <>
          <div className="card p-6 bg-[#F8F9FA]">
            <div className="overline mb-2">SAY THIS OUT LOUD NOW</div>
            <p className="text-sm text-[#0A0A0A] italic leading-relaxed mb-6">&ldquo;{testPhrase}&rdquo;</p>
            <div className="overline mb-2 text-[#EE2737]">● RECORDING · AUTO-STOPS AT 5 SEC</div>
            {/* Live VU meter */}
            <div className="w-full h-3 bg-[#E5E7EB] overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#059669] via-[#F59E0B] to-[#EE2737] transition-[width] duration-100"
                style={{ width: `${Math.round(micLevel * 100)}%` }}
                data-testid="miccheck-vu"
              />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#878787] mt-2">
              {micLevel > 0.05 ? "Voice detected — keep going" : "Speak louder / move closer to the mic"}
            </div>
          </div>
        </>
      )}

      {phase === "uploading" && (
        <div className="card p-6 flex items-center gap-3" data-testid="miccheck-uploading">
          <Loader2 className="animate-spin text-[#EE2737]" size={20} />
          <span className="text-sm text-[#525252]">Transcribing your audio (~3s)…</span>
        </div>
      )}

      {phase === "result" && (
        <>
          <div className="card p-6" data-testid="miccheck-result">
            <div className="overline mb-2">WHAT WE HEARD</div>
            {transcript ? (
              <p className="text-lg text-[#0A0A0A] leading-relaxed italic">&ldquo;{transcript}&rdquo;</p>
            ) : (
              <p className="text-sm text-[#878787]">(no speech detected)</p>
            )}
            <div className="text-xs text-[#525252] mt-3">
              {wordCount} word{wordCount === 1 ? "" : "s"} captured in 5 seconds.
              {!looksGood && " Try speaking closer to the mic, or move to a quieter room."}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <button onClick={onPass} disabled={proceedDisabled} className="btn btn-primary justify-center w-full sm:w-auto disabled:opacity-60 disabled:cursor-wait" data-testid="miccheck-proceed">
              <CheckCircle2 size={14} /> {looksGood ? proceedLabel : "Continue anyway"}
            </button>
            <button onClick={retry} className="btn btn-secondary justify-center w-full sm:w-auto" data-testid="miccheck-retry">
              <RefreshCw size={14} /> Try mic check again
            </button>
          </div>
        </>
      )}
    </div>
  );
}
